import {
  GAINSIGHT_SCRIPT_SDK_LINK,
  GAINSIGHT_TAG_KEY,
} from "@/constants/environment.constants";
import { GAINSIGHT_FEATURE_FLAGS } from "@/constants/gainsight.constants";
import { CurrentCompany } from "@/hooks/useCurrentCompany/useCurrentCompany.decl";
import { CurrentCompanyBilling } from "@/hooks/useCurrentCompanyBilling/useCurrentCompanyBilling.decl";
import { CurrentUser } from "@/hooks/useCurrentUser/useCurrentUser.decl";
import { FeaturesFlagsType } from "@/hooks/useGetFeaturesFlags/useGetFeaturesFlags.decl";
import { getEnv } from "../environment/environment.helpers";

export const generateIdByEnv = (id: string): string =>
  getEnv() !== "production" ? `${id}_${getEnv()}` : id;

/**
 * Filters feature flags based on the GAINSIGHT_FEATURE_FLAGS constant
 */
export const flattenFeatureFlags = (
  featureFlags: FeaturesFlagsType
): Record<string, number | boolean> =>
  Object.entries(featureFlags).reduce(
    (acc: Record<string, number | boolean>, [key, value]) => {
      if (GAINSIGHT_FEATURE_FLAGS.includes(key)) {
        acc[`ff_${key}`] = value;
      }
      return acc;
    },
    {}
  );

export const loadGainsightScript = (): Promise<() => void> =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = GAINSIGHT_SCRIPT_SDK_LINK;
    script.async = true;
    document.body.appendChild(script);

    // Initialization of the Gainsight PX script
    window.aptrinsic =
      window.aptrinsic ||
      /* istanbul ignore next */

      function () {
        /* istanbul ignore next */
        window.aptrinsic.q = window.aptrinsic.q || [];
        /* istanbul ignore next */

        window.aptrinsic.q.push(arguments);
      };
    window.aptrinsic.p = GAINSIGHT_TAG_KEY;

    const cleanup = () => {
      document.body.removeChild(script);
    };
    resolve(cleanup);
  });

export const identify = (
  user: CurrentUser,
  company: CurrentCompany,
  billing: CurrentCompanyBilling | undefined,
  isTrial: boolean,
  featureFlags: FeaturesFlagsType
): void => {
  const flattenedFeatureFlags = flattenFeatureFlags(featureFlags);
  window?.aptrinsic?.(
    "identify",
    {
      // User Fields
      id: generateIdByEnv(user.ID), // Required for logged in app users
      userId: user.ID, // Not required but this one is needed
      name: user.fullName,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      roles: user.roles.map(({ name }) => name),
      trial: isTrial,
      plan: company.plan,
      createOn: user.createdAt,
      city: billing?.address?.city,
      country: billing?.address?.country,
      state: billing?.address?.state,
      zipCode: billing?.address?.zip,
      language: user.language,
    },
    {
      // Account Fields
      id: generateIdByEnv(company.id.toString()), // Required for logged in app users
      companyId: generateIdByEnv(company.id.toString()),
      companyIdNumber: company.id, // Used for numerical comparisons
      ...flattenedFeatureFlags,
    }
  );
};

export const track = (eventName: string, properties: object = {}): void => {
  window?.aptrinsic?.("track", eventName, properties);
};

export const setGlobalContext = (context: object): void => {
  window?.aptrinsic?.("set", "globalContext", context);
};
